/**
 * Define feature flag here as constants.
 *
 * export const KB_ENHANCEMENTS = 'jsd.px.kb-enhancements';
 *
 * Use them in react components.
 * import { KB_ENHANCEMENTS } from '@helpcenter/feature-flags';
 * import { FeatureFlag } from 'view/common/feature-flag';
 *
 * <FeatureFlag name={KB_ENHANCEMENTS}>
 *  I will be shown in true :-)
 * </FeatureFlag>
 *
 * ========== NOTE ===================
 * BE CAREFUL ADDING FEATURE FLAGS, IF YOU RENAME THEM IT WILL CHANGE THE ANALYTIC CONTEXT
 * NAME AND NO LONGER SEND THE SAME CONTEXT ATTRIBUTE NAME.
 * ===================================
 */

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
// Be careful when using these constants directly. In most cases, you should use the function.
export const RICH_TEXT_AREA_FIELD = 'jsd-use-adf-default-value-for-textarea';

const ENABLE_KB_ARTICLES_OPEN_IN_SAME_TAB = 'make-kb-articles-open-in-same-tab';
const KB_ARTICLE_LINK_NAVIGATION_ANALYTICS = 'kb_article_link_navigation_analytics';

export const UNIFIED_HELP_ADD_FILTER_FOR_CUSTOMER_ACCOUNTS =
	'unified-help-add-filter-for-customer-accounts';

const CSM_ACCESS_CONTROL_SPLIT = 'csm_access_control_split_take2';
const AI_WIDGET_TEAM25 = 'berry-5094-ai-widget-support-website-team25';
const CSM_ENABLE_FORGE_CUSTOM_FIELD_JSM_CONTEXT_TOKEN =
	'csm_enable_forge_custom_field_jsm_context_token';
const SEND_RAW_HELP_CENTER_TYPE_IN_ANALYTICS = 'help-center-fix-type-in-analytics';

export const JSM_VIRTUAL_AGENT_ANONYMOUS_ACCESS = 'anonymous_virtual_agent_access';

const FIX_RELAY_ID_CONFLICT_IN_HELP_CENTER = 'fix_relay_id_conflict_in_help_center';
const FIX_RELAY_ID_CONFLICT_FOR_HELP_OBJECT_STORE = 'fix_relay_id_conflict_for_help_object_store';
export const JSM_SCALE_60K_AGG_BRANCH_OVERRIDE_IN_HELP_CENTER =
	'jsm_scale_60k_agg_branch_override_in_help_center';

const TUNING_RETRY_MECHANISM_FOR_MODELS_ENDPOINT = 'tuning_model_retries';
const PROJECT_MAPPING_ENABLED_IN_MULTI_HC = 'enable_project_mapping_for_help_center';
const ENABLE_CROSS_SITE_CONFLUENCE = 'enable-cross-site-confluence';
const PROFORMA_TTVC_UPDATE_PACKAGE_ENABLED = 'measure_proforma_ttvc_impact';
const CONSUME_LOOSELY_LAZY_MANIFEST_IN_SSR = 'consume_loosely_lazy_manifest_in_ssr';
const DEFAULT_HC_USING_GQL_BRANDING = 'default_hc_using_gql_branding';
export const HELP_CENTER_REFRESH_FLAG = 'enable_increased_flexibility'; // TODO: using same key as increased flexibility for bundle release
export const EMAIL_VERIFIED_SIGNAL_ON_CUSTOMERS_PAGE =
	'sptn-1697-email-verified-signal-on-customers-page';
export const ENABLE_INCREASED_FLEXIBILITY = 'enable_increased_flexibility';
export const ENABLE_FOLLOWUP_QUESTIONS_ON_PORTAL =
	'enable_followup_questions_on_omni_channel_portal';
export const HELP_CENTER_REQUEST_FORM_CANCEL_GO_BACK = 'help_center_request_form_cancel_go_back';
const REQUEST_CREATE_DROPDOWN_INITIAL_OPTIONS_CALL = 'request_create_dropdown_initial_options_call';
const HIDE_KB_COLUMN_PROJECT_BREAKDOWN = 'hc_vsa_settings_hide_kb_article_column';
const VSA_SETTINGS_PERFORMANCE_IMPROVEMENTS = 'hc_vsa_settings_performance_improvements';
const TOTAL_ARTICLE_COUNT = 'hc_show_kb_total_article_count';
const VSA_ADOPTION_TOUCHPOINT_HELP_CENTER = 'vsa-adoption-touchpoint-help-center';
const HIDE_VSA_ENDPOINTS = 'hide_vsa_endpoints';
const VSA_ADOPTION_CHAT_SIDE_BAR = 'vsa-adoption-portal-chat-side-bar';
const ENABLE_HUBS_IN_HELP_CENTER = 'enable-hubs-in-help-center';
const ENABLE_MULTI_SELECT_CF_UIM_SUPPORT = 'ditto_uim_jsm_multi_select_support';
const JSM_PORTAL_ROVO_INTEGRATION = 'jsm_portal_rovo_integration';
const JSM_DATEPICKER_FIELD_SUPPORT = 'ditto_uim_jsm_datepicker_field_support';
const JSM_SURVEYS_CORE_FEATURE = 'jsm_surveys_core_feature';
const JSM_SURVEYS_GRAPHQL_API = 'jsm_surveys_graphql_api';
const JSM_SURVEYS_VALIDATION_ERROR_PAYLOAD = 'jsm_surveys_validation_error_payload';
// Shared with the Jira side, which gates survey link enrichment on the same gate.
const JSM_SURVEYS_WORK_ITEM_SUPPORT = 'enable-surveys-work-item-support';
// Portal-only (jsm-portal_web) counterpart of JSM_SURVEYS_WORK_ITEM_SUPPORT, which is not
// registered for the portal target app.
const SEND_WORK_ITEM_ID_IN_PORTAL_SURVEY = 'send_workitem_id_in_portal_survey';
const SURVEY_LANDING_ANALYTICS = 'add_analytics_for_survey_visited_with_work_item_';

const ROVO_AGENTS_TOOLS_CONFIG_M2 = 'rovo_agents_tools_config_m2';

const REQUEST_LIST_INFINITE_SCROLL = 'portal-6922-request-list-jsis-pagination';
const APPROVAL_LIST_INFINITE_SCROLL = 'portal-approval-list-jsis-pagination';

export const isEmailVerifiedSignalEnabled = () => {
	return FeatureGates.checkGate(EMAIL_VERIFIED_SIGNAL_ON_CUSTOMERS_PAGE);
};

// TODO: remove this code, we are marking this true given timeline for LD Deprecation
export const isWrmOptimizationsEnabled = () => true;

export const isRichTextAreaEnabled = () => FeatureGates.checkGate(RICH_TEXT_AREA_FIELD);

export const islooselyLazyJsErrorsToSentryEnabled = () =>
	FeatureGates.checkGate('throw_lazy_error_boundary_js_errors_to_sentry');

/*
 * Cleaning up the LD feature flag 'jsm-portal-custom-theming'
 * https://app.launchdarkly.com/jira/production/features/jsm-portal-custom-theming
 * Custom themes were not rolled out.
 *
 * */
export const isCustomThemingEnabled = () => false;

export const isProjectMappingInHelpCenterEnabled = () => {
	return FeatureGates.checkGate(PROJECT_MAPPING_ENABLED_IN_MULTI_HC);
};

export const isUnifiedHelpAddFilterForCustomerAccounts = () => {
	return FeatureGates.checkGate(UNIFIED_HELP_ADD_FILTER_FOR_CUSTOMER_ACCOUNTS);
};

export const isFixRelayIdConflictInHelpCenter = () => {
	return FeatureGates.checkGate(FIX_RELAY_ID_CONFLICT_IN_HELP_CENTER);
};

export const isFixRelayIdConflictForHelpObjectStoreEnabled = () => {
	return FeatureGates.checkGate(FIX_RELAY_ID_CONFLICT_FOR_HELP_OBJECT_STORE);
};

export const isJsmScale60KAggBranchOverrideInHelpCenterEnabled = (): boolean => {
	return FeatureGates.checkGate(JSM_SCALE_60K_AGG_BRANCH_OVERRIDE_IN_HELP_CENTER);
};

export const isSendRawHelpCenterTypeInAnalyticsEnabled = () => {
	return FeatureGates.checkGate(SEND_RAW_HELP_CENTER_TYPE_IN_ANALYTICS);
};

export const isCsmAccessControlSplitEnabled = () => {
	return FeatureGates.checkGate(CSM_ACCESS_CONTROL_SPLIT);
};

export const isAiWidgetTeam25Enabled = () => {
	return FeatureGates.checkGate(AI_WIDGET_TEAM25);
};

// CSM Kalari feature flags
export const isUpdateArticlesLinkCsmEnabled = () => {
	return FeatureGates.checkGate('update_articles_links_csm');
};

export const isCsmAppification = () => {
	return FeatureGates.checkGate('csm_appification_standalone_navigation');
};

export const isCsmForgeCustomFieldJsmContextTokenEnabled = () => {
	return FeatureGates.checkGate(CSM_ENABLE_FORGE_CUSTOM_FIELD_JSM_CONTEXT_TOKEN);
};

export const isCrossSiteConnieConnectionEnabled = () => {
	return FeatureGates.checkGate(ENABLE_CROSS_SITE_CONFLUENCE);
};

export const isKbArticlesSameTabRedirectionEnabled = () => {
	return FeatureGates.checkGate(ENABLE_KB_ARTICLES_OPEN_IN_SAME_TAB);
};

export const isKbArticleLinkNavigationAnalyticsEnabled = () => {
	return FeatureGates.checkGate(KB_ARTICLE_LINK_NAVIGATION_ANALYTICS);
};

export const isVirtualAgentHeaderButtonEnabled = () => {
	return FeatureGates.checkGate(VSA_ADOPTION_TOUCHPOINT_HELP_CENTER);
};

export const isRetryMechanismTuningEnabled = () => {
	return FeatureGates.checkGate(TUNING_RETRY_MECHANISM_FOR_MODELS_ENDPOINT);
};

export const isProformaTTVCUpdateMeasureEnabled = () => {
	return FeatureGates.checkGate(PROFORMA_TTVC_UPDATE_PACKAGE_ENABLED);
};

export const isVAOnHelpCenterGAEnabled = () => {
	return FeatureGates.checkGate('va_on_help_center_ga');
};

export const isConsumeLooselyLazyManifestInSSREnabled = () => {
	return FeatureGates.checkGate(CONSUME_LOOSELY_LAZY_MANIFEST_IN_SSR);
};

export const isMigrateToAnalyticsBridgeEnabled = () => {
	return FeatureGates.checkGate('jsm-portal-migrate-to-analytics-bridge');
};

export const isDefaultHCUsingGqlBrandingCalls = () => {
	return FeatureGates.checkGate(DEFAULT_HC_USING_GQL_BRANDING);
};

export const isUseHelpCenterRefresh = () => {
	return FeatureGates.checkGate(HELP_CENTER_REFRESH_FLAG);
};

export const isDraftArticleSuggestionsEnabled = () => {
	return FeatureGates.checkGate('ai-answer-using-draft-article');
};

// Ditto FCF (start)

export const isForgeCustomFieldsOnPortalViewEnabled = () =>
	FeatureGates.checkGate('forge_custom_fields_on_jsm_portal_view');

// Ditto FCF (end)

// Ditto UIM (start)

export const isUiModificationsSupportEnabled = () =>
	FeatureGates.checkGate('ditto_uim_-_jsm_support_main');

export const isTextFieldSupportEnabled = () => {
	return FeatureGates.checkGate('uim_jsm_textfield_support');
};

export const isNumberFieldSupportEnabled = () => {
	return FeatureGates.checkGate('ditto_uim_jsm_number_field_support');
};

export const isMultiCheckboxSupportEnabled = () => {
	return FeatureGates.checkGate('ditto_uim_jsm_multi_checkbox_support');
};

export const isRadioButtonsSupportEnabled = () => {
	return FeatureGates.checkGate('ditto_uim_jsm_radio_buttons_support');
};

export const isUserPickerEmailSupportEnabled = () => {
	return FeatureGates.checkGate('ditto_uim_jsm_user_picker_email');
};

export const isDateTimeSupportEnabled = () => {
	return FeatureGates.checkGate('ditto_uim_jsm_datetime_field_support');
};

export const isDueDateSupportEnabled = () => {
	return FeatureGates.checkGate('uim_jsm_duedate_support');
};

export const isCascadingSelectSupportEnabled = () => {
	return FeatureGates.checkGate('ditto_uim_jsm_cascading_select_support');
};

export const isUserPickerSupportEnabled = () => {
	return FeatureGates.checkGate('ditto_uim_jsm_user_picker_support');
};

export const cascadingSelectDefaultValueSupportEnabled = () => {
	return FeatureGates.checkGate('ditto_uim_jsm_cascading_default_value_support');
};

// Ditto UIM (end)

export const isFollowupEnabledForPortal = () => {
	return FeatureGates.checkGate(ENABLE_FOLLOWUP_QUESTIONS_ON_PORTAL);
};

export const isAiAnswer24HourTimeoutEnabled = () => {
	return FeatureGates.checkGate('ai_answer_default_timeout_to_24_hours');
};

export const isIncreasedFlexibilityEnabled = () => {
	return FeatureGates.checkGate(ENABLE_INCREASED_FLEXIBILITY);
};

export const isElementRendererSplitEnabled = () => {
	return FeatureGates.checkGate('enable_element_renderer_split');
};

export const isOmnichannelEnabledForHC = () => {
	return FeatureGates.checkGate('enable_omni_channel_for_helpcenter');
};

export const isItsmIssueRenamingEnabled = () => {
	return FeatureGates.checkGate('itsm-issue-renaming');
};

export const isItsmProjectRenamingEnabled = () => {
	return FeatureGates.checkGate('itsm-project-renaming');
};

export const isOmnichannelGreetingAPIEnabled = () => {
	return FeatureGates.checkGate('jsm_chat_orchestrator_greeting_api_enabled');
};

export const isOmnichannelSubscriptionErrorpageEnabled = () => {
	return FeatureGates.checkGate('enable_omnichannel_subscription_error_page');
};

export const isOmnichannelAccessibilityFixesEnabled = () => {
	return FeatureGates.checkGate('helpcenter_omnichannel_accessibility_fixes');
};

export const isRequestCreateSkipDropdownInitialOptionsCallEnabled = () => {
	return FeatureGates.checkGate(REQUEST_CREATE_DROPDOWN_INITIAL_OPTIONS_CALL);
};

export const isRequestFormCancelGoBackEnabled = (): boolean =>
	FeatureGates.checkGate(HELP_CENTER_REQUEST_FORM_CANCEL_GO_BACK);

export const isHideKbColumnEnabled = () => {
	return FeatureGates.checkGate(HIDE_KB_COLUMN_PROJECT_BREAKDOWN);
};

export const isVsaSettingsPerformanceImprovementsEnabled = () => {
	return FeatureGates.checkGate(VSA_SETTINGS_PERFORMANCE_IMPROVEMENTS);
};

export const isTotalArticleCountEnabled = () => {
	return FeatureGates.checkGate(TOTAL_ARTICLE_COUNT);
};

export const isHideVsaEndpointsEnabled = () => {
	return FeatureGates.checkGate(HIDE_VSA_ENDPOINTS);
};

export const isVsaAdoptionPortalChatSideBarEnabled = () => {
	return FeatureGates.checkGate(VSA_ADOPTION_CHAT_SIDE_BAR);
};

export const isHubsEnabledInHelpCenter = () => {
	return FeatureGates.checkGate(ENABLE_HUBS_IN_HELP_CENTER);
};

export const isMultiSelectEnabled = () => {
	return FeatureGates.checkGate(ENABLE_MULTI_SELECT_CF_UIM_SUPPORT);
};

export const isJsmSurveysCoreFeatureEnabled = () => {
	return FeatureGates.checkGate(JSM_SURVEYS_CORE_FEATURE);
};

export const isJsmSurveysValidationErrorPayloadEnabled = (): boolean =>
	FeatureGates.checkGate(JSM_SURVEYS_VALIDATION_ERROR_PAYLOAD);

/**
 * Whether the surveys AGG GraphQL data path is enabled for the current
 * tenant. Reuses the backend's own jsm_surveys_graphql_api gate directly -
 * there is no separate frontend gate.
 */
export const isJsmSurveysGraphqlApiEnabled = () => {
	return FeatureGates.checkGate(JSM_SURVEYS_GRAPHQL_API);
};

export const isSurveysWorkItemSupportEnabled = (): boolean => {
	return FeatureGates.checkGate(JSM_SURVEYS_WORK_ITEM_SUPPORT);
};

export const isSendWorkItemIdInPortalSurveyEnabled = (): boolean => {
	return FeatureGates.checkGate(SEND_WORK_ITEM_ID_IN_PORTAL_SURVEY);
};

export const isSurveyLandingAnalyticsEnabled = (): boolean =>
	FeatureGates.checkGate(SURVEY_LANDING_ANALYTICS);

export const isJsmPortalRovoIntegrationEnabled = () => {
	return FeatureGates.checkGate(JSM_PORTAL_ROVO_INTEGRATION);
};

export const isDatePickerSupportEnabled = () => {
	return FeatureGates.checkGate(JSM_DATEPICKER_FIELD_SUPPORT);
};

export const isRovoAgentsToolsConfigM2Enabled = () => {
	return FeatureGates.checkGate(ROVO_AGENTS_TOOLS_CONFIG_M2);
};

/**
 * Feature gate: rovo-service-agent-publish-on-create
 *
 * With agent versioning, a newly created Rovo service agent is a draft and is non-functional until
 * published. When on, the one-click create flow publishes the agent before connecting the widget,
 * and the agent selector lists only published editable agents.
 *
 * @rollout In progress
 * @owner Servo
 * @ticket NO-ISSUE
 */
export const ROVO_SERVICE_AGENT_PUBLISH_ON_CREATE = 'rovo-service-agent-publish-on-create';

export const isRovoServiceAgentPublishOnCreateEnabled = () => {
	return FeatureGates.checkGate(ROVO_SERVICE_AGENT_PUBLISH_ON_CREATE);
};

export const isStudioGsnAndScenarioRefreshEnabled = () => {
	return FeatureGates.checkGate('studio-gsn-and-scenario-refresh');
};

export const isSkipProjectSectionInInstructionsEnabled = () => {
	return FeatureGates.checkGate('rsa-skip-project-section-in-instructions');
};

export const isRequestListInfiniteScrollEnabled = () => {
	return FeatureGates.checkGate(REQUEST_LIST_INFINITE_SCROLL);
};

export const isApprovalListInfiniteScrollEnabled = () => {
	return FeatureGates.checkGate(APPROVAL_LIST_INFINITE_SCROLL);
};

/**
 * Gates the cookie-preferences button in the help center footer.
 *
 * @rollout In progress
 * @owner Dropbears
 * @ticket BEARS-3044
 */
const FOOTER_COOKIE_PREFERENCES = 'service-collection-customers-cookie-preferences';

export const isFooterCookiePreferencesEnabled = (): boolean =>
	FeatureGates.checkGate(FOOTER_COOKIE_PREFERENCES);

export const isDateSelectionFixForOmnichannelEnabled = (): boolean =>
	FeatureGates.checkGate('fix_date_selection_for_omnichannel');
